import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDiaryContext } from "../../../../../../hooks/useDiaryContext";
import { useAppContext } from "../../../../../../hooks/useAppContext";
import { useNotificationsContext } from "../../../../../../hooks/useNotificationsContext";
import { useBookingPermissions } from "../../../../../../hooks/useBookingPermissions";
import { postUpdateBookingV4 } from "../../../../../../APIs/Booking/UpdateBooking";
import { BookingRequirement } from "../../../../../../types/VenueGroup";
import { BookingV4 } from "../../../../../../types/bookings";
import {
  formatRequirementValue,
  RequirementValue
} from "../../../../../../utils/bookingRequirements";
import { RequirementField } from "./RequirementField";
import { CollapsibleSection } from "../../../../../blocks/CollapsibleSection";

type UpdateBookingRequirementsPayload = Pick<
  BookingV4,
  "_id" | "booking_requirements"
>;
type RequirementValues = Record<string, RequirementValue>;

const DEBOUNCE_DELAY = 300;
const ERROR_TOAST_DURATION = 4000;

const getInitialValues = (
  requirements: BookingRequirement[],
  bookingRequirements?: BookingV4["booking_requirements"]
): RequirementValues => {
  const values: RequirementValues = {};
  requirements.forEach((req) => {
    const existingValue = bookingRequirements?.[req.name];
    if (existingValue !== undefined) {
      values[req.name] = existingValue;
    } else {
      switch (req.type) {
        case "boolean":
          values[req.name] = false;
          break;
        case "multiselect":
          values[req.name] = [];
          break;
        default:
          values[req.name] = "";
      }
    }
  });
  return values;
};

const SetUpRequirements = () => {
  const diaryContext = useDiaryContext();
  const { selectedBooking, selectedBookingV4, setDiaryContext } = diaryContext;
  const { venueGroupConfig } = useAppContext();
  const notificationsContext = useNotificationsContext();
  const { userCanManageBooking: canEdit } = useBookingPermissions();
  const requirements = useMemo(
    () => venueGroupConfig?.booking_requirements ?? [],
    [venueGroupConfig?.booking_requirements]
  );

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousValuesRef = useRef<RequirementValues>({});
  const localValuesRef = useRef<RequirementValues>({});
  const selectedBookingV4Ref = useRef(selectedBookingV4);
  const mountedRef = useRef(true);
  const isSavingRef = useRef(false);
  const saveRef = useRef<(v: RequirementValues) => Promise<void>>();

  const [localValues, setLocalValues] = useState<RequirementValues>(() =>
    getInitialValues(requirements, selectedBookingV4?.booking_requirements)
  );

  useEffect(() => {
    localValuesRef.current = localValues;
  }, [localValues]);

  useEffect(() => {
    selectedBookingV4Ref.current = selectedBookingV4;

    if (isSavingRef.current || debounceTimer.current) {
      return;
    }
    const incomingValues = getInitialValues(
      requirements,
      selectedBookingV4?.booking_requirements
    );
    const currentValues = localValuesRef.current;
    const keysMatch =
      Object.keys(incomingValues).length === Object.keys(currentValues).length;
    const valuesMatch =
      keysMatch &&
      Object.entries(incomingValues).every(([key, val]) => {
        const cur = currentValues[key];
        if (Array.isArray(val) && Array.isArray(cur)) {
          return val.length === cur.length && val.every((v, i) => v === cur[i]);
        }
        return val === cur;
      });
    if (!valuesMatch) {
      setLocalValues(incomingValues);
    }
  }, [selectedBookingV4, requirements]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
        saveRef.current?.(localValuesRef.current);
      }
    };
  }, []);

  const rollbackValues = useCallback(
    (currentBooking: BookingV4) => {
      if (!mountedRef.current) return;
      setLocalValues(previousValuesRef.current);
      setDiaryContext({
        selectedBookingV4: {
          ...currentBooking,
          booking_requirements: previousValuesRef.current
        }
      });
      notificationsContext.addNotification({
        heading: "Failed to update set up requirements",
        autoClose: ERROR_TOAST_DURATION,
        toastType: "error"
      });
    },
    [setDiaryContext, notificationsContext]
  );

  const saveRequirements = useCallback(
    async (updatedValues: RequirementValues): Promise<void> => {
      const currentBooking = selectedBookingV4Ref.current;
      if (!currentBooking?._id) {
        return;
      }

      isSavingRef.current = true;

      const bookingPayload: UpdateBookingRequirementsPayload = {
        _id: currentBooking._id,
        booking_requirements: updatedValues
      };

      try {
        const response = await postUpdateBookingV4(bookingPayload as BookingV4);

        if (!mountedRef.current) return;

        if (!response) {
          rollbackValues(currentBooking);
          return;
        }

        setDiaryContext({
          selectedBookingV4: {
            ...currentBooking,
            ...response,
            booking_requirements: response.booking_requirements ?? updatedValues
          }
        });
      } catch {
        if (!mountedRef.current) return;
        rollbackValues(currentBooking);
      } finally {
        isSavingRef.current = false;
      }
    },
    [setDiaryContext, rollbackValues]
  );

  useEffect(() => {
    saveRef.current = saveRequirements;
  }, [saveRequirements]);

  const handleFieldChange = useCallback(
    (name: string, value: RequirementValue) => {
      if (!selectedBookingV4Ref.current) {
        return;
      }

      if (!debounceTimer.current) {
        previousValuesRef.current = { ...localValuesRef.current };
      }

      const updatedValues = {
        ...localValuesRef.current,
        [name]: value
      };

      setLocalValues(updatedValues);

      setDiaryContext({
        selectedBookingV4: {
          ...selectedBookingV4Ref.current,
          booking_requirements: updatedValues
        }
      });

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null;
        saveRequirements(updatedValues);
      }, DEBOUNCE_DELAY);
    },
    [setDiaryContext, saveRequirements]
  );

  const summary = useMemo(() => {
    const parts: string[] = [];
    requirements.forEach((req) => {
      const val = localValues[req.name];
      if (val === undefined || val === null || val === "" || val === false)
        return;
      if (req.type === "boolean" && val === true) {
        parts.push(req.label);
      } else if (req.type !== "boolean") {
        parts.push(
          `${req.label}: ${formatRequirementValue(req.type, val, req)}`
        );
      }
    });
    return parts.length > 0 ? parts.join(" \u00b7 ") : "No requirements set";
  }, [requirements, localValues]);

  if (selectedBooking && !selectedBookingV4) {
    return null;
  }

  if (requirements.length === 0) {
    return null;
  }

  return (
    <CollapsibleSection
      title="Set Up Requirements"
      badge={String(requirements.length)}
      summary={summary}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {requirements.map((requirement) => (
          <RequirementField
            key={requirement.name}
            requirement={requirement}
            value={localValues[requirement.name] ?? ""}
            canEdit={canEdit}
            onChange={handleFieldChange}
          />
        ))}
      </div>
    </CollapsibleSection>
  );
};

export default SetUpRequirements;
